import React, { type PropsWithChildren } from "react";

import clsx from "clsx";
import {
  type Control,
  type UseFormRegister,
  useFormState,
} from "react-hook-form";

import { type Option } from "@/types/react-select";
import { GenericTooltip } from "@components/tooltip-container";
import { type BenchmarkTypeEnum } from "@north-beam/nb-client-side";
import { Select } from "@shared/selects";
import { TextInput } from "@shared/text-input";

import { useHasUnsavedChangesAtom } from "../atoms";

import { type ChooseAndSetValidationTargets } from "./benchmark-choose-and-set-modal";
import { BenchmarkHelpWidget } from "./benchmark-help";

export const BenchmarkSubStep = ({
  children,
  stepNumber,
  title,
  subHeader,
  tooltip,
  className,
}: PropsWithChildren<{
  stepNumber: number | string;
  title: string;
  subHeader?: string;
  tooltip?: React.ReactNode;
  className?: string;
}>) => {
  return (
    <div className={clsx("flex-1", className)}>
      <div className="text-xs font-semibold mb-1">STEP {stepNumber}</div>
      <div>
        <div className="font-semibold text-sm xl:text-base">
          {title}
          {tooltip && <GenericTooltip content={tooltip} />}
        </div>
        {subHeader && <div className="text-xxs xl:text-xs">{subHeader}</div>}
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
};

export const BenchmarkSubstepWithInput = ({
  stepNumber,
  title,
  subHeader,
  benchmarkType,
  disabled,
  placeholder,
  loading,
  tooltip,
  control,
  register,
  cellRegistration,
}: {
  tooltip: string;
  loading: boolean;
  stepNumber: string;
  title: string;
  benchmarkType: BenchmarkTypeEnum;
  subHeader?: string;
  disabled: boolean;
  placeholder: string;
  control: Control<ChooseAndSetValidationTargets>;
  register: UseFormRegister<ChooseAndSetValidationTargets>;
  cellRegistration: Parameters<
    UseFormRegister<ChooseAndSetValidationTargets>
  >[0];
}) => {
  const { errors } = useFormState({ control });
  const error = errors[cellRegistration];
  const [, setHasUnsavedChanges] = useHasUnsavedChangesAtom();

  const { onChange, ...rest } = register(cellRegistration);
  return (
    <BenchmarkSubStep
      title={title}
      stepNumber={stepNumber}
      tooltip={tooltip}
      subHeader={subHeader}
    >
      <TextInput
        {...rest}
        onChange={(e) => {
          onChange(e);
          setHasUnsavedChanges(true);
        }}
        leftIcon={
          benchmarkType === "CAC" ? (
            <i className="fa-regular fa-sharp fa-dollar-sign" />
          ) : null
        }
        inputClassName={clsx({
          "border-[#ef4444] focus:border-[hsl(0deg_0%_80%)]": error,
          "border-[hsl(0deg_0%_80%)]": !error,
        })}
        type="text"
        placeholder={placeholder}
        disabled={disabled}
      />
      {error && (
        <div
          className={clsx(
            "font-light text-xs mt-3 border",
            "rounded-[0.25rem] border-danger bg-[#FDF2F0] p-3",
          )}
        >
          {error.type === "too_small" && "Value must be greater than 0"}
          {error.type === "invalid_type" && "Invalid numeric value"}
          <BenchmarkHelpWidget className="mt-4" size="xs" />
        </div>
      )}
    </BenchmarkSubStep>
  );
};

export const BenchmarkSubstepWithOptions = <T,>({
  testId,
  className,
  stepNumber,
  title,
  options,
  currentValue,
  tooltip,
  disable,
  onChange,
  isOptionDisabled,
  subHeader,
  placeholder,
}: {
  testId?: string;
  className?: string;
  placeholder: string;
  onChange: (option: Option<T>["value"] | null) => void;
  disable: boolean;
  stepNumber: number;
  title: string;
  options: Option<T>[];
  currentValue: Option<T>["value"] | null;
  tooltip?: React.ReactNode;
  subHeader?: string;
  isOptionDisabled?: (option: Option<T>) => boolean;
}) => {
  return (
    <BenchmarkSubStep
      title={title}
      stepNumber={stepNumber}
      tooltip={tooltip}
      subHeader={subHeader}
      className={className}
    >
      <Select
        testId={testId}
        options={options.map((option) => ({
          ...option,
          disabled: isOptionDisabled?.(option),
        }))}
        onChange={onChange}
        isDisabled={disable}
        placeholder={placeholder}
        value={currentValue}
      />
    </BenchmarkSubStep>
  );
};
